// ===========================================
// BUSTED! BLACKJACK - ADMIN PANEL PAGE
// ===========================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, AdminFeedbackItem, AdminUserItem, AdminSurveyItem } from '../api';
import { formatBalance } from '../utils/format';
import bustedLogo from '../assets/busted.png';
import { useAdminLogStore, AdminLogEntry, LogCategory, EventLogEntry } from '../stores/adminLogStore';

type Tab = 'dashboard' | 'feedback' | 'users' | 'surveys' | 'logs';

interface DashboardStats {
  totalUsers: number;
  usersToday: number;
  totalHands: number;
  totalFeedback: number;
  supportRequests: number;
  totalRevenue: number;
  totalSurveys: number;
}

export function AdminPanelPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [feedback, setFeedback] = useState<AdminFeedbackItem[]>([]);
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [surveys, setSurveys] = useState<AdminSurveyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch data based on active tab
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        if (activeTab === 'dashboard' && !stats) {
          const result = await api.admin.getStats();
          if (result.success && result.data) {
            setStats(result.data.stats);
          } else {
            setError(result.error?.message || 'Failed to load stats');
          }
        } else if (activeTab === 'feedback' && feedback.length === 0) {
          const result = await api.admin.getFeedback();
          if (result.success && result.data) {
            setFeedback(result.data.feedback);
          } else {
            setError(result.error?.message || 'Failed to load feedback');
          }
        } else if (activeTab === 'users' && users.length === 0) {
          const result = await api.admin.getUsers();
          if (result.success && result.data) {
            setUsers(result.data.users);
          } else {
            setError(result.error?.message || 'Failed to load users');
          }
        } else if (activeTab === 'surveys' && surveys.length === 0) {
          const result = await api.admin.getSurveys();
          if (result.success && result.data) {
            setSurveys(result.data.surveys);
          } else {
            setError(result.error?.message || 'Failed to load surveys');
          }
        }
      } catch (err) {
        setError('An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [activeTab, stats, feedback.length, users.length, surveys.length]);

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={bustedLogo}
                alt="BUSTED!"
                className="h-[50px] cursor-pointer"
                onClick={() => navigate('/home')}
              />
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-sm text-gray-400">Manage BUSTED! Blackjack</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1">
            {(['dashboard', 'feedback', 'users', 'surveys', 'logs'] as Tab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeTab === tab
                    ? 'text-casino-gold-400 border-b-2 border-casino-gold-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'logs' ? 'Admin Logs' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-casino-gold-400"></div>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <StatCard
                  title="Total Users"
                  value={stats.totalUsers.toLocaleString()}
                  subtitle={`+${stats.usersToday} today`}
                  icon="👥"
                />
                <StatCard
                  title="Total Hands"
                  value={stats.totalHands.toLocaleString()}
                  icon="🃏"
                />
                <StatCard
                  title="Total Revenue"
                  value={`$${stats.totalRevenue.toFixed(2)}`}
                  icon="💰"
                  highlight
                />
                <StatCard
                  title="Feedback Received"
                  value={stats.totalFeedback.toString()}
                  icon="💬"
                />
                <StatCard
                  title="Support Requests"
                  value={stats.supportRequests.toString()}
                  icon="🎧"
                />
                <StatCard
                  title="Surveys Completed"
                  value={stats.totalSurveys.toString()}
                  icon="📋"
                />
              </div>
            )}

            {/* Feedback Tab */}
            {activeTab === 'feedback' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Feedback & Support Requests ({feedback.length})
                </h2>

                {feedback.length === 0 ? (
                  <div className="bg-gray-800 rounded-lg p-8 text-center">
                    <p className="text-gray-400">No feedback yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {feedback.map((item) => (
                      <div
                        key={item.id}
                        className={`bg-gray-800 rounded-lg p-6 border-l-4 ${
                          item.type === 'support' ? 'border-red-500' : 'border-blue-500'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {item.avatarUrl ? (
                              <img
                                src={item.avatarUrl}
                                alt={item.displayName}
                                className="w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold">
                                {item.username.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-white">{item.displayName}</p>
                              <p className="text-sm text-gray-400">@{item.username}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                item.type === 'support'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}
                            >
                              {item.type}
                            </span>
                            <p className="text-sm text-gray-500 mt-1">
                              {formatDate(item.createdAt)}
                            </p>
                          </div>
                        </div>

                        <div className="mb-3">
                          <span className="text-casino-gold-400 text-lg">
                            {renderStars(item.rating)}
                          </span>
                        </div>

                        <p className="text-gray-300 whitespace-pre-wrap">{item.message}</p>

                        {(item.contactEmail || item.contactPhone) && (
                          <div className="mt-4 pt-4 border-t border-gray-700 flex gap-4 text-sm">
                            {item.contactEmail && (
                              <a
                                href={`mailto:${item.contactEmail}`}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                {item.contactEmail}
                              </a>
                            )}
                            {item.contactPhone && (
                              <span className="text-gray-400">{item.contactPhone}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div>
                <h2 className="text-xl font-semibold text-white mb-4">
                  Users ({users.length})
                </h2>

                <div className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">
                            User
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">
                            Provider
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">
                            Balance
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">
                            Hands
                          </th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-300">
                            Win Rate
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">
                            Joined
                          </th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-300">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-700/50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {user.avatarUrl ? (
                                  <img
                                    src={user.avatarUrl}
                                    alt={user.displayName}
                                    className="w-8 h-8 rounded-full"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold text-sm">
                                    {user.username.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-white">{user.displayName}</p>
                                  <p className="text-xs text-gray-400">
                                    @{user.username}#{user.discriminator}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-gray-300 capitalize">{user.provider}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-casino-gold-400 font-mono">
                                {formatBalance(user.balance)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-gray-300">
                              {user.handsPlayed.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-300">
                              {user.winRate.toFixed(1)}%
                            </td>
                            <td className="px-4 py-3 text-gray-400 text-sm">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-2">
                                {user.isAdmin && (
                                  <span className="px-2 py-0.5 bg-casino-gold-500/20 text-casino-gold-400 text-xs rounded">
                                    Admin
                                  </span>
                                )}
                                {user.isBanned && (
                                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">
                                    Banned
                                  </span>
                                )}
                                {!user.isAdmin && !user.isBanned && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                                    Active
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Surveys Tab */}
            {activeTab === 'surveys' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-4">
                  Survey Responses ({surveys.length})
                </h2>

                {surveys.length === 0 ? (
                  <div className="bg-gray-800 rounded-lg p-8 text-center">
                    <p className="text-gray-400">No survey responses yet</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {surveys.map((survey) => (
                      <SurveyResponseCard
                        key={survey.id}
                        survey={survey}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Admin Logs Tab */}
            {activeTab === 'logs' && <AdminLogsTab />}
          </>
        )}
      </main>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: string;
  highlight?: boolean;
}

function StatCard({ title, value, subtitle, icon, highlight }: StatCardProps) {
  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 text-sm">{title}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className={`text-3xl font-bold ${highlight ? 'text-green-400' : 'text-white'}`}>
        {value}
      </p>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// Survey Response Card Component
interface SurveyResponseCardProps {
  survey: AdminSurveyItem;
  formatDate: (date: string) => string;
}

// Question labels for display
const QUESTION_LABELS: Record<string, string> = {
  q1_clarity: 'First impression clarity',
  q2_bet_understanding: 'Understood betting',
  q3_first_impression: 'Visual design impression',
  q4_pace: 'Game pace',
  q5_card_animations: 'Card animations',
  q6_chip_animations: 'Chip animations',
  q7_controls_intuitive: 'Controls intuitive',
  q8_confusing_rules: 'Confusing rules',
  q9_sound_effects: 'Sound effects',
  q10_table_design: 'Table design',
  q11_text_readable: 'Text readable',
  q12_used_hints: 'Used Play Hints',
  q13_hints_helpful: 'Hints helpful',
  q14_subscribe_likelihood: 'Subscribe likelihood',
  q15_value_24h: 'Value: 24h/$1',
  q15_value_30d: 'Value: 30d/$5',
  q15_value_1yr: 'Value: 1yr/$25',
  q16_buddy_features: 'Desired Buddy features',
  q16_buddy_features_other: 'Other Buddy features',
  q17_devices: 'Devices used',
  q17_devices_other: 'Other devices',
  q18_browsers: 'Browsers used',
  q18_browsers_other: 'Other browsers',
  q19_performance: 'Performance issues',
  q20_broken_animations: 'Broken animations',
  q21_starting_balance: 'Starting balance',
  q22_daily_chips: 'Daily chips',
  q23_purchase_chips: 'Would purchase chips',
  q24_multiplayer_importance: 'Multiplayer importance',
  q25_invite_friends: 'Would invite friends',
  q26_social_features: 'Social features wanted',
  q27_nps: 'NPS score',
  q28_fix_first: 'Fix first',
  q29_play_more: 'Would play more if',
  q30_other_feedback: 'Other feedback',
};

function SurveyResponseCard({ survey, formatDate }: SurveyResponseCardProps) {
  const [expanded, setExpanded] = useState(false);
  const responses = survey.responses as Record<string, unknown>;

  // Calculate some summary stats
  const nps = responses.q27_nps as number || 0;
  const cardAnim = responses.q5_card_animations as number || 0;
  const chipAnim = responses.q6_chip_animations as number || 0;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      {/* Header - always visible */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {survey.avatarUrl ? (
              <img
                src={survey.avatarUrl}
                alt={survey.displayName || survey.username || 'User'}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-casino-gold-500 flex items-center justify-center text-gray-900 font-bold">
                {(survey.username || survey.email || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-medium text-white">
                {survey.displayName || survey.username || survey.email}
              </p>
              <p className="text-sm text-gray-400">
                {survey.lifetimeHandsPlayed?.toLocaleString() || 0} hands played
                {survey.totalDollarsSpent && Number(survey.totalDollarsSpent) > 0 && (
                  <span className="text-green-400 ml-2">${Number(survey.totalDollarsSpent).toFixed(2)} spent</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Quick stats */}
            <div className="hidden sm:flex items-center gap-3 text-sm">
              <span className={`px-2 py-1 rounded ${nps >= 9 ? 'bg-green-500/20 text-green-400' : nps >= 7 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                NPS: {nps}/10
              </span>
              <span className="text-gray-400">Cards: {cardAnim}/5</span>
              <span className="text-gray-400">Chips: {chipAnim}/5</span>
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-700 p-4">
          {/* Player Stats Section */}
          <div className="mb-6 p-4 bg-gray-900/50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Player Stats at Time of Survey</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Member Since</span>
                <span className="text-gray-300">
                  {survey.memberSince ? new Date(survey.memberSince).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Provider</span>
                <span className="text-gray-300 capitalize">{survey.provider || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Balance</span>
                <span className="text-casino-gold-400">{formatBalance(survey.balanceAtSurvey || 0)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Hands Played</span>
                <span className="text-gray-300">{survey.lifetimeHandsPlayed?.toLocaleString() || 0}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Win Rate</span>
                <span className="text-gray-300">{survey.lifetimeWinRate?.toFixed(1) || 0}%</span>
              </div>
              <div>
                <span className="text-gray-500 block">Blackjacks</span>
                <span className="text-gray-300">{survey.lifetimeBlackjacks?.toLocaleString() || 0}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Total Wagered</span>
                <span className="text-gray-300">{formatBalance(survey.lifetimeTotalWagered || 0)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Net Result</span>
                <span className={survey.lifetimeNetResult && survey.lifetimeNetResult >= 0 ? 'text-green-400' : 'text-red-400'}>
                  {survey.lifetimeNetResult && survey.lifetimeNetResult >= 0 ? '+' : ''}{formatBalance(survey.lifetimeNetResult || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">$ Spent</span>
                <span className="text-green-400">${Number(survey.totalDollarsSpent || 0).toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Purchases</span>
                <span className="text-gray-300">{survey.purchaseCount || 0}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Chips Bought</span>
                <span className="text-gray-300">{formatBalance(survey.totalChipsPurchased || 0)}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Buddy</span>
                <span className={survey.hasBuddyActive ? 'text-green-400' : 'text-gray-500'}>
                  {survey.hasBuddyActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Survey Responses */}
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Survey Responses</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(responses).map(([key, value]) => {
              // Skip empty values
              if (value === '' || value === 0 || (Array.isArray(value) && value.length === 0)) {
                return null;
              }

              const label = QUESTION_LABELS[key] || key;
              let displayValue: string;

              if (Array.isArray(value)) {
                displayValue = value.join(', ');
              } else if (typeof value === 'number') {
                displayValue = value.toString();
              } else {
                displayValue = String(value);
              }

              // Determine if this is a text response (longer content)
              const isTextResponse = typeof value === 'string' && value.length > 50;

              return (
                <div
                  key={key}
                  className={`${isTextResponse ? 'md:col-span-2' : ''}`}
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                  <p className={`text-gray-300 ${isTextResponse ? 'whitespace-pre-wrap' : ''}`}>
                    {displayValue}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Metadata Section */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Device & Location Metadata</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {/* Location */}
              {(survey.country || survey.city) && (
                <div>
                  <span className="text-gray-500">Location: </span>
                  <span className="text-gray-300">
                    {[survey.city, survey.region, survey.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
              {survey.countryCode && (
                <div>
                  <span className="text-gray-500">Country Code: </span>
                  <span className="text-gray-300">{survey.countryCode}</span>
                </div>
              )}
              {survey.isp && (
                <div>
                  <span className="text-gray-500">ISP: </span>
                  <span className="text-gray-300">{survey.isp}</span>
                </div>
              )}
              {survey.ipAddress && (
                <div>
                  <span className="text-gray-500">IP: </span>
                  <span className="text-gray-300">{survey.ipAddress}</span>
                </div>
              )}

              {/* Device */}
              {(survey.screenWidth && survey.screenHeight) && (
                <div>
                  <span className="text-gray-500">Screen: </span>
                  <span className="text-gray-300">{survey.screenWidth}x{survey.screenHeight}</span>
                </div>
              )}
              {(survey.viewportWidth && survey.viewportHeight) && (
                <div>
                  <span className="text-gray-500">Viewport: </span>
                  <span className="text-gray-300">{survey.viewportWidth}x{survey.viewportHeight}</span>
                </div>
              )}
              {survey.devicePixelRatio && (
                <div>
                  <span className="text-gray-500">DPR: </span>
                  <span className="text-gray-300">{survey.devicePixelRatio}x</span>
                </div>
              )}
              {survey.isTouchDevice !== null && (
                <div>
                  <span className="text-gray-500">Touch: </span>
                  <span className="text-gray-300">{survey.isTouchDevice ? 'Yes' : 'No'}</span>
                </div>
              )}

              {/* Browser */}
              {survey.timezone && (
                <div>
                  <span className="text-gray-500">TZ: </span>
                  <span className="text-gray-300">{survey.timezone}</span>
                </div>
              )}
              {survey.language && (
                <div>
                  <span className="text-gray-500">Lang: </span>
                  <span className="text-gray-300">{survey.language}</span>
                </div>
              )}
              {survey.connectionType && (
                <div>
                  <span className="text-gray-500">Connection: </span>
                  <span className="text-gray-300">{survey.connectionType}</span>
                </div>
              )}

              {/* Hardware */}
              {survey.hardwareConcurrency && (
                <div>
                  <span className="text-gray-500">CPU Cores: </span>
                  <span className="text-gray-300">{survey.hardwareConcurrency}</span>
                </div>
              )}
              {survey.deviceMemory && (
                <div>
                  <span className="text-gray-500">Memory: </span>
                  <span className="text-gray-300">{survey.deviceMemory}GB</span>
                </div>
              )}
              {survey.colorDepth && (
                <div>
                  <span className="text-gray-500">Color: </span>
                  <span className="text-gray-300">{survey.colorDepth}-bit</span>
                </div>
              )}
            </div>

            {/* Full User Agent */}
            {survey.userAgent && (
              <div className="mt-3">
                <p className="text-xs text-gray-500">User Agent</p>
                <p className="text-xs text-gray-400 break-all mt-1">{survey.userAgent}</p>
              </div>
            )}

            {/* Referrer */}
            {survey.referrer && (
              <div className="mt-2">
                <p className="text-xs text-gray-500">Referrer</p>
                <p className="text-xs text-gray-400 break-all">{survey.referrer}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// ADMIN LOGS TAB COMPONENT
// ===========================================
// Displays all admin logs (animation failures, batch timeouts, errors)
// grouped by similarity to prevent log flooding

function AdminLogsTab() {
  const [categoryFilter, setCategoryFilter] = useState<LogCategory | 'all'>('all');
  const logs = useAdminLogStore((state) => state.getLogs(categoryFilter === 'all' ? undefined : categoryFilter));
  const totalCount = useAdminLogStore((state) => state.totalCount);
  const categoryCounts = useAdminLogStore((state) => state.getCategoryCounts());
  const clearLogs = useAdminLogStore((state) => state.clearLogs);
  const clearCategory = useAdminLogStore((state) => state.clearCategory);
  const deleteLog = useAdminLogStore((state) => state.deleteLog);

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getCategoryColor = (category: LogCategory) => {
    switch (category) {
      case 'animation':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      case 'batch':
        return 'bg-red-500/20 text-red-400 border-red-500';
      case 'network':
        return 'bg-blue-500/20 text-blue-400 border-blue-500';
      case 'error':
        return 'bg-orange-500/20 text-orange-400 border-orange-500';
      case 'event':
        return 'bg-purple-500/20 text-purple-400 border-purple-500';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500';
    }
  };

  const getSeverityColor = (severity: AdminLogEntry['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-600/30 text-red-300';
      case 'error':
        return 'bg-red-500/20 text-red-400';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getCategoryIcon = (category: LogCategory) => {
    switch (category) {
      case 'animation':
        return '🎬';
      case 'batch':
        return '📦';
      case 'network':
        return '🌐';
      case 'error':
        return '❌';
      case 'event':
        return '⏭️';
      default:
        return '📋';
    }
  };

  const renderLogDetails = (log: AdminLogEntry) => {
    switch (log.category) {
      case 'animation':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-gray-700">
            <div>
              <p className="text-gray-500 text-xs uppercase">Animation Type</p>
              <p className="text-gray-300">{log.details.animationType}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Target</p>
              <p className="text-gray-300">{log.details.targetDescription}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Expected Duration</p>
              <p className="text-gray-300">{formatDuration(log.details.expectedDuration)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Reason</p>
              <p className="text-gray-300 uppercase">{log.details.reason}</p>
            </div>
          </div>
        );
      case 'batch':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-4 pt-4 border-t border-gray-700">
            <div>
              <p className="text-gray-500 text-xs uppercase">Event Count</p>
              <p className="text-gray-300">{log.details.eventCount}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Timeout Duration</p>
              <p className="text-gray-300">{formatDuration(log.details.timeoutDuration)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Last Processed</p>
              <p className="text-gray-300">{log.details.lastProcessedEvent || 'N/A'} ({log.details.lastProcessedIndex ?? -1}/{log.details.eventCount})</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Event Types</p>
              <p className="text-gray-300 text-xs truncate" title={log.details.eventTypes.join(', ')}>
                {log.details.eventTypes.slice(0, 3).join(', ')}{log.details.eventTypes.length > 3 ? '...' : ''}
              </p>
            </div>
          </div>
        );
      case 'network':
        return (
          <div className="grid grid-cols-2 gap-4 text-sm mt-4 pt-4 border-t border-gray-700">
            <div>
              <p className="text-gray-500 text-xs uppercase">Reason</p>
              <p className="text-gray-300 uppercase">{log.details.reason}</p>
            </div>
            {log.details.attemptCount !== undefined && (
              <div>
                <p className="text-gray-500 text-xs uppercase">Attempts</p>
                <p className="text-gray-300">{log.details.attemptCount}</p>
              </div>
            )}
          </div>
        );
      case 'error':
        return (
          <div className="mt-4 pt-4 border-t border-gray-700">
            {log.details.stack && (
              <div className="mb-3">
                <p className="text-gray-500 text-xs uppercase mb-1">Stack Trace</p>
                <pre className="text-xs text-gray-400 font-mono bg-gray-900 p-2 rounded overflow-x-auto max-h-32">
                  {log.details.stack}
                </pre>
              </div>
            )}
            {log.details.context && Object.keys(log.details.context).length > 0 && (
              <div>
                <p className="text-gray-500 text-xs uppercase mb-1">Context</p>
                <pre className="text-xs text-gray-400 font-mono bg-gray-900 p-2 rounded overflow-x-auto">
                  {JSON.stringify(log.details.context, null, 2)}
                </pre>
              </div>
            )}
          </div>
        );
      case 'event': {
        const details = log.details as EventLogEntry['details'];
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4 pt-4 border-t border-gray-700">
            <div>
              <p className="text-gray-500 text-xs uppercase">Event Type</p>
              <p className="text-gray-300">{details.eventType}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Reason</p>
              <p className="text-gray-300 uppercase">{details.reason}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Event Seq</p>
              <p className="text-gray-300">{details.eventSeq}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Last Processed Seq</p>
              <p className="text-gray-300">{details.lastProcessedSeq}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Event Round</p>
              <p className="text-gray-300">{details.eventRound}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs uppercase">Active Round</p>
              <p className="text-gray-300">{details.activeRound}</p>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Admin Logs
          </h2>
          <p className="text-sm text-gray-400">
            {logs.length} unique log entries ({totalCount} total occurrences)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {logs.length > 0 && (
            <button
              onClick={() => categoryFilter === 'all' ? clearLogs() : clearCategory(categoryFilter)}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors text-sm"
            >
              Clear {categoryFilter === 'all' ? 'All' : categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1)}
            </button>
          )}
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            categoryFilter === 'all'
              ? 'bg-casino-gold-500 text-gray-900'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          All ({totalCount})
        </button>
        {(['event', 'batch', 'animation', 'network', 'error'] as LogCategory[]).map((category) => (
          <button
            key={category}
            onClick={() => setCategoryFilter(category)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors flex items-center gap-1 ${
              categoryFilter === category
                ? getCategoryColor(category).replace('border-', 'ring-1 ring-')
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <span>{getCategoryIcon(category)}</span>
            <span className="capitalize">{category}</span>
            <span className="text-xs opacity-75">({categoryCounts[category]})</span>
          </button>
        ))}
      </div>

      {logs.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <div className="text-4xl mb-4">✨</div>
          <p className="text-gray-400">No {categoryFilter === 'all' ? '' : categoryFilter + ' '}logs recorded</p>
          <p className="text-sm text-gray-500 mt-2">
            Logs are recorded for skipped events, animation failures, batch timeouts, network issues, and errors
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => (
            <div
              key={log.key}
              className={`bg-gray-800 rounded-lg p-4 border-l-4 ${getCategoryColor(log.category).split(' ')[2]}`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{getCategoryIcon(log.category)}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${getSeverityColor(log.severity)}`}>
                      {log.severity}
                    </span>
                    <span className="text-white font-medium">
                      {log.functionName}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {log.fileName}
                  </p>
                  <p className="text-sm text-gray-300 mt-1">
                    {log.description}
                  </p>
                </div>
                <div className="text-right flex items-start gap-2">
                  <div>
                    <div className="text-2xl font-bold text-red-400">
                      {log.count.toLocaleString()}×
                    </div>
                    <p className="text-xs text-gray-500">occurrences</p>
                  </div>
                  <button
                    onClick={() => deleteLog(log.key)}
                    className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    title="Delete this log entry"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Category-specific details */}
              {renderLogDetails(log)}

              {/* Error message if present (for animation/batch) */}
              {(log.category === 'animation' || log.category === 'batch') && log.details.errorMessage && (
                <div className="mt-4 p-3 bg-red-500/10 rounded border border-red-500/20">
                  <p className="text-xs text-red-400 font-mono break-all">
                    {log.details.errorMessage}
                  </p>
                </div>
              )}

              {/* Time info */}
              <div className="mt-3 text-xs text-gray-500 flex gap-4">
                <span>First: {formatTimestamp(log.firstOccurrence)}</span>
                {log.count > 1 && <span>Last: {formatTimestamp(log.lastOccurrence)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminPanelPage;
